import React, { useState, useEffect } from "react";
import { Clock, AlertCircle, LogOut, ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { examApi, submissionApi } from "../services/api";
import { useAuth } from "../context/AuthContext";

function TakeExam() {
  const navigate = useNavigate();
  const { examId } = useParams();
  const { user } = useAuth();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [examStarted, setExamStarted] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [timerInitialized, setTimerInitialized] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [assignedQuestions, setAssignedQuestions] = useState([]);

  // Helper functions and variables
  const studentEmail = user?.email || "";
  const studentName = user?.displayName || "Student";
  const isAuthenticated = !!user && user.role === "student";
  
  const getExamInProgressKey = (examId, email) => {
    return `exam_in_progress_${examId}_${email}`;
  };

  // Check for student email on mount and fetch display name
  useEffect(() => {
    if (!user) {
      navigate("/student/login");
      return;
    }
  }, [user, navigate]);

  // Check for existing exam in progress
  useEffect(() => {
    if (!user || !examId) return;
    // Restored state would be handled by submission API
  }, [examId, user]);

  // Fetch exam data
  useEffect(() => {
    if (!user || !examId) {
      navigate("/exam-selection");
      return;
    }

    const fetchExamAndStart = async () => {
      try {
        setLoading(true);
        setError("");
        // Fetch exam details
        const examData = await examApi.getExam(examId);
        setExam(examData);
        
        // Set assigned questions from exam data
        if (examData.questions) {
          setAssignedQuestions(examData.questions);
        }
        
        // Start exam (create submission)
        const submission = await submissionApi.startExam(examId);
        setSubmission(submission);
        setTimeLeft(examData.duration_minutes * 60); // Convert to seconds
        setExamStarted(true);
      } catch (err) {
        console.error("Error loading exam:", err);
        setError(err.message || "Failed to load exam. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchExamAndStart();
  }, [user, examId, navigate]);

  // Add beforeunload event listener to warn when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (examStarted && !submitted && !loading) {
        e.preventDefault();
        e.returnValue =
          "Are you sure you want to leave? Your exam will be automatically submitted.";
        return "Are you sure you want to leave? Your exam will be automatically submitted.";
      }
    };

    // Prevent browser back button navigation
    const handlePopState = (e) => {
      if (examStarted && !submitted && !loading) {
        e.preventDefault();
        window.history.pushState(null, "", window.location.pathname);
        alert(
          "You cannot leave the exam once started. Your exam will be automatically submitted if you try to leave.",
        );
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Push current state to prevent back navigation
    if (examStarted && !submitted) {
      window.history.pushState(null, "", window.location.pathname);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [examStarted, submitted, loading]);

  const handleSubmit = React.useCallback(async () => {
    if (submitted || submitting || !submission) return;

    setSubmitting(true);

    try {
      const submissionAnswers = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer,
      }));
      
      await submissionApi.submitExam(submission.id, submissionAnswers);
      setSubmitted(true);
      alert("Exam submitted successfully! Thank you for participating.");
      setTimeout(() => {
        navigate("/student/results");
      }, 2000);
    } catch (error) {
      alert("Failed to submit exam. Please try again.");
      console.error("Error submitting exam:", error);
    } finally {
      setSubmitting(false);
    }
  }, [submitted, submitting, submission, answers, navigate]);

  // Timer effect: only run if timerInitialized and not loading
  useEffect(() => {
    if (
      !timerInitialized ||
      loading ||
      submitted ||
      !exam ||
      typeof timeLeft !== "number" ||
      timeLeft <= 0
    )
      return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        const newTime = prev - 1;
        // Update localStorage with new time
        if (examStarted) {
          const examState = JSON.parse(
            localStorage.getItem(getExamInProgressKey(examId, studentEmail)) ||
              "{}",
          );
          examState.timeLeft = newTime;
          localStorage.setItem(
            getExamInProgressKey(examId, studentEmail),
            JSON.stringify(examState),
          );
        }
        return newTime;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [
    timeLeft,
    submitted,
    exam,
    examStarted,
    examId,
    studentEmail,
    timerInitialized,
    loading,
    handleSubmit,
  ]);

  const handleLogout = () => {
    if (examStarted && !submitted) {
      setShowLogoutConfirm(true);
    } else {
      localStorage.removeItem("studentEmail");
      if (studentEmail)
        localStorage.removeItem(getExamInProgressKey(examId, studentEmail));
      navigate("/auth-email");
    }
  };

  const handleConfirmLogout = async () => {
    try {
      // Auto-submit the exam before logout
      if (examStarted && !submitted) {
        await examApi.submitExam(examId, answers);
      }
      localStorage.removeItem("studentEmail");
      if (studentEmail)
        localStorage.removeItem(getExamInProgressKey(examId, studentEmail));
      navigate("/auth-email");
    } catch (error) {
      console.error("Error auto-submitting exam:", error);
      // Still logout even if auto-submit fails
      localStorage.removeItem("studentEmail");
      if (studentEmail)
        localStorage.removeItem(getExamInProgressKey(examId, studentEmail));
      navigate("/auth-email");
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleBackToExams = () => {
    if (examStarted && !submitted) {
      alert(
        "You cannot leave the exam once started. Your exam will be automatically submitted if you try to leave.",
      );
      return;
    }
    navigate("/exam-selection");
  };

  const handleAnswerChange = (questionId, value) => {
    const newAnswers = {
      ...answers,
      [questionId]: value,
    };
    setAnswers(newAnswers);

    // Update localStorage with new answers
    if (examStarted) {
      const examState = JSON.parse(
        localStorage.getItem(getExamInProgressKey(examId, studentEmail)) ||
          "{}",
      );
      examState.answers = newAnswers;
      localStorage.setItem(
        getExamInProgressKey(examId, studentEmail),
        JSON.stringify(examState),
      );
    }
  };

  // Format timeLeft as mm:ss
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    console.log("[TakeExam] Rendering loading spinner");
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log("[TakeExam] Rendering error:", error);
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
          <button
            onClick={handleBackToExams}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Available Exams
          </button>
        </div>
      </div>
    );
  }

  if (!exam) {
    console.log("[TakeExam] No exam data found");
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-600">No exam data found</p>
          <button
            onClick={handleBackToExams}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Available Exams
          </button>
        </div>
      </div>
    );
  }

  console.log("[TakeExam] Rendering exam UI");
  return (
    <div className="container mx-auto px-4 py-4 sm:py-8">
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
        {/* Student Name and Logout */}
        <div className="mb-2 flex justify-between items-center">
          <span className="text-base sm:text-lg font-semibold text-blue-700">
            {studentName}
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-red-600 hover:text-red-800 text-sm"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        {/* Exam Header */}
        <div className="border-b pb-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div className="w-full sm:w-auto">
              {!examStarted && (
                <button
                  onClick={handleBackToExams}
                  className="flex items-center text-blue-600 hover:text-blue-800 mb-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Exams
                </button>
              )}
              <h1 className="text-xl sm:text-2xl font-bold mb-2">
                {exam.title}
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">
                {exam.description}
              </p>
              {examStarted && !submitted && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                  ⚠️ You cannot leave this page once the exam has started. Your
                  exam will be automatically submitted if you try to leave.
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-red-600">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">
                  {formatTime(timeLeft)}
                </span>
              </div>
              {submitted && (
                <div className="flex items-center gap-2 text-green-600">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">Submitted</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Instructions */}
        {exam.instructions && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <h2 className="font-semibold text-sm sm:text-base mb-2">
              Instructions
            </h2>
            <p className="text-gray-700 text-sm sm:text-base whitespace-pre-line">
              {exam.instructions}
            </p>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-6 sm:space-y-8">
          {assignedQuestions.map((question, idx) => (
            <div key={question._id} className="border-b pb-4 sm:pb-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-medium">
                  Question {idx + 1}
                </h3>
              </div>
              <p className="text-sm sm:text-base mb-3 sm:mb-4">
                {question.question}
              </p>
              <div className="space-y-2">
                {question.options.map((option, optionIndex) => (
                  <label
                    key={optionIndex}
                    className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer text-sm sm:text-base"
                  >
                    <input
                      type="radio"
                      name={`question-${question._id}`}
                      value={optionIndex}
                      checked={answers[question._id] === optionIndex.toString()}
                      onChange={(e) =>
                        handleAnswerChange(question._id, e.target.value)
                      }
                      disabled={submitted}
                      className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Submit Button */}
        {!submitted && (
          <div className="mt-6 sm:mt-8">
            <button
              onClick={handleSubmit}
              disabled={
                submitted ||
                submitting ||
                timeLeft <= 0 ||
                !timerInitialized ||
                loading
              }
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 text-sm sm:text-base"
            >
              {submitting ? "Submitting..." : "Submit Exam"}
            </button>
          </div>
        )}
      </div>

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Logout</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to logout? Your exam will be automatically
              submitted with your current answers.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConfirmLogout}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Logout & Submit
              </button>
              <button
                onClick={handleCancelLogout}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TakeExam;
